<template>
    <div itemscope itemtype="http://schema.org/Thing" class="bkr-cc imgmodal">
        <div class="bkr-cc single-carousel owl-carousel owl-theme owl-single-item mt-0" id="imageGallery" ref="single">
            <div v-for="(image, index) in singleImages" :key="index" class="prop-1-1 slide-owl-wrap bk-ai-hint-host">
                <a :href="getLightboxUrl(image)" :data-fancybox="'single-item-image' + _uid" :data-alt="getAltText(image)" :data-image-classes="getMetadata(image).classes" :data-video-ratio="getVideoRatioAttr(image)" :class="{'largeVideoThumb': image.position < 0}">
                    <img
                        v-if="shouldRenderDirectImage(image, index)"
                        class="mw-100 h-auto"
                        :class="getMetadata(image).classes"
                        :src="getImageInitialSrc(image)"
                        :alt="getAltText(image)"
                        :title="getImageName(image)"
                        :width="getImageWidth(image)"
                        :height="getImageHeight(image)"
                        fetchpriority="high"
                        loading="eager"
                        decoding="sync"
                        @error="handleDirectImageError($event, image)">
                    <lazy-img
                        v-else-if="image.position >= 0 && shouldLoadMainSlide(index)"
                        :index="index"
                        :image-flags="getMetadata(image).flags"
                        :alt="getAltText(image)"
                        :image-url="getDisplayUrl(image)"
                        :title="getImageName(image)"
                        :width="getImageWidth(image)"
                        :height="getImageHeight(image)" />
                    <div v-else-if="image.position >= 0" class="mw-100 h-auto"></div>
                    <img class="owl-lazy" v-else :data-src="image.thumb" alt="Video abspielen" />
                </a>
                <ai-image-hint v-if="shouldRenderDirectImage(image, index) && currentItem === index && isCurrentImageAi" />
            </div>
        </div>
        <div v-if="showThumbs" id="thumb-carousel" class="owl-thumbs owl-carousel owl-theme owl-single-item" ref="thumbs">
            <div class="prop-1-1" v-for="(imagePreview, idx) in carouselImages" :key="idx">
                <div class="image-container" @click="goTo(idx)">
                    <lazy-img
                        v-if="shouldLoadThumb(idx)"
                        :show-ai-hint="false"
                        :picture-class="imagePreview.class"
                        :image-flags="getMetadata(imagePreview).flags"
                        v-bind:class="{ 'active': currentItem === idx, 'videoButton': imagePreview.position < 0 }"
                        :image-url="imagePreview.displayUrl"
                        :alt="getAltText(imagePreview)"
                        :title="getImageName(imagePreview)"
                        :width="getImageWidth(imagePreview)"
                        :height="getImageHeight(imagePreview)">
                    </lazy-img>
                    <div
                        v-else
                        :class="[imagePreview.class, { 'active': currentItem === idx, 'videoButton': imagePreview.position < 0 }]">
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import { getImageMetadata } from "../../helper/imageMetadata";
import { isNullOrUndefined } from "../../helper/utils";

const DEFAULT_VIDEO_RATIO = 16 / 9;

// YouTube has no "original aspect ratio" thumbnail for standard 16:9 videos and answers oardefault.jpg
// with HTTP 404 - but the response body is still a valid, decodable 120x90 JPEG (a static "no
// thumbnail" placeholder, byte-identical for every such video), so an <img>/Image() element fires
// "load" for it just like for a real thumbnail. Since Image() cannot see the HTTP status at all,
// the only way to tell "no OAR thumbnail" apart from "a real 120x90 OAR thumbnail" (which YouTube
// never actually serves - originals are full/near-full resolution) is this fixed placeholder size.
const YOUTUBE_NO_RATIO_PLACEHOLDER_WIDTH = 120;
const YOUTUBE_NO_RATIO_PLACEHOLDER_HEIGHT = 90;

export default {
    name: "item-image-carousel",

    props: {
        maxQuantity:
        {
            type: Number,
            default: 10
        },
        videoCarouselPosition:
        {
            type: Number,
            default: 1
        },
        imageUrlAccessor:
        {
            type: String,
            default: "url"
        },
        showThumbs:
        {
            type: Boolean,
            default: true
        },
        showDots:
        {
            type: Boolean,
            default: true
        },
        animationStyle:
        {
            type: String,
            default: "standard"
        },
        pluginPath:
        {
            type: String,
            default: ""
        }
    },

    inject: {
        itemId: {
            default: null
        }
    },

    data()
    {
        return {
            currentItem: 0,
            initialized: false,
            hasCarouselInteracted: false,
            currentThumbStart: 0,
            videoRatios: {}
        };
    },

    computed:
    {
        currentImage()
        {
            return this.singleImages[this.currentItem] || null;
        },

        isCurrentImageAi()
        {
            return !!this.currentImage && this.currentImage.position >= 0 && this.getMetadata(this.currentImage).isAi;
        },

        currentVariation()
        {
            return this.$store.getters[`${this.itemId}/currentItemVariation`]
        },

        youtubeVideos()
        {
            let videoIdString = "";
            if (isNullOrUndefined(this.currentVariation.variationProperties))
                return [];

            for (const property of this.currentVariation.variationProperties) {
                for (const prop of property.properties) {
                    if (prop.id === 192)
                        videoIdString = prop.values.value.trim();
                }
            }

            return videoIdString
                .split(",")
                .map(entry => entry.trim())
                .filter(entry => entry !== "")
                .map(entry =>
                {
                    // An aspect ratio may be appended to the video id to skip the auto detection,
                    // e.g. "a5RUk9iQT4w|9:16" or "a5RUk9iQT4w|0.5625".
                    const [id, ratio] = entry.split("|").map(part => part.trim());

                    return {
                        id: id,
                        ratio: this.parseVideoRatio(ratio) || this.videoRatios[id] || null
                    };
                })
                .filter(video => !!video.id);
        },

        youtubeVideoIds()
        {
            return this.youtubeVideos.map(video => video.id);
        },

        carouselImages()
        {
            const carouselImages = this.getPreparedImages("urlPreview");

            const carouselVideos = [];
            if (this.videoThumbUrl && this.youtubeVideos.length > 0) {
                let i = -1;
                for (const video of this.youtubeVideos)
                {
                    const videoBtn = {
                        url: 'https://img.youtube.com/vi/'+ video.id +'/mqdefault.jpg',
                        displayUrl: 'https://img.youtube.com/vi/'+ video.id +'/mqdefault.jpg',
                        class: 'owl-thumb border-appearance videoButton',
                        alternate: 'Video abspielen',
                        position: i,
                        name: ''
                    };
                    carouselVideos.push(videoBtn);
                    i--;
                }
            }
            
            // Insert on the n-th position
            carouselImages.splice(this.videoCarouselPosition, 0, ...carouselVideos);

            return carouselImages;
        },

        singleImages()
        {
            let images = this.getPreparedImages(this.imageUrlAccessor);

            let videos = [];
            if (this.videoThumbUrl && this.youtubeVideos.length > 0) {
                let i = -1;
                for (const video of this.youtubeVideos) {
                    const videoBtn = {
                        url: 'https://www.youtube.com/watch?v='+ video.id,
                        thumb: 'https://img.youtube.com/vi/' + video.id +'/maxresdefault.jpg',
                        alternate: 'Video abspielen',
                        position: i,
                        name: '',
                        videoRatio: video.ratio
                    };
                    videos.push(videoBtn);
                    i--;
                }
            }
            // Insert after 1st element
            images.splice(this.videoCarouselPosition, 0, ...videos);

            return images;
        },
        videoThumbUrl()
        {
            const hasProps = this.currentVariation.variationProperties?.find(prop => prop.id === 4);
            // Return the properties array filtered by id 192, if it exists
            const videoUrl = hasProps ? hasProps.properties.filter(prop => prop.id === 192) : [];

            return videoUrl.length > 0;
        }
    },

    watch: {
        currentVariation:
        {
            handler(val, oldVal)
            {
                if (val !== oldVal)
                {
                    this.currentItem = 0;
                    this.detectVideoRatios();
                    setTimeout(() =>
                    {
                        this.reInitialize();
                    }, 1);
                }
            },
            deep: true
        }
    },

    mounted()
    {
        this.$nextTick(() =>
        {
            this.initCarousel();
            this.initThumbCarousel();
            this.detectVideoRatios();
        });
    },

    methods:
    {
        parseVideoRatio(value)
        {
            if (!value)
            {
                return null;
            }

            const parts = (value + "").split(/[:/]/);

            if (parts.length === 2)
            {
                const width = parseFloat(parts[0]);
                const height = parseFloat(parts[1]);

                return width > 0 && height > 0 ? width / height : null;
            }

            const ratio = parseFloat(value);

            return ratio > 0 ? ratio : null;
        },

        detectVideoRatios()
        {
            for (const video of this.youtubeVideos)
            {
                if (video.ratio || !isNullOrUndefined(this.videoRatios[video.id]))
                {
                    continue;
                }

                this.probeVideoRatio(video.id);
            }
        },

        // YouTube only serves a real "original aspect ratio" thumbnail for videos that are not 16:9;
        // for standard videos oardefault.jpg resolves to a fixed-size placeholder image (see
        // YOUTUBE_NO_RATIO_PLACEHOLDER_* above), which we treat the same as a failed request.
        probeVideoRatio(videoId)
        {
            if (typeof Image === "undefined")
            {
                return;
            }

            // Marks the video as "detection running" so a variation switch does not queue a second probe.
            this.$set(this.videoRatios, videoId, 0);

            const probe = new Image();

            probe.onload = () =>
            {
                const isPlaceholder = probe.naturalWidth === YOUTUBE_NO_RATIO_PLACEHOLDER_WIDTH
                    && probe.naturalHeight === YOUTUBE_NO_RATIO_PLACEHOLDER_HEIGHT;

                const ratio = !isPlaceholder && probe.naturalWidth > 0 && probe.naturalHeight > 0
                    ? probe.naturalWidth / probe.naturalHeight
                    : 0;

                this.$set(this.videoRatios, videoId, ratio > 0 ? ratio : DEFAULT_VIDEO_RATIO);
            };

            probe.onerror = () =>
            {
                this.$set(this.videoRatios, videoId, DEFAULT_VIDEO_RATIO);
            };

            probe.src = `https://i.ytimg.com/vi/${videoId}/oardefault.jpg`;
        },

        getVideoRatioAttr(image)
        {
            if (!image || image.position >= 0 || !image.videoRatio)
            {
                return null;
            }

            return image.videoRatio.toFixed(4);
        },

        getImageCount()
        {
            return this.carouselImages.length > this.maxQuantity ? this.maxQuantity : this.carouselImages.length;
        },

        reInitialize()
        {
            this.currentItem = 0;
            this.currentThumbStart = 0;
            this.initialized = false;

            const $owl = $(this.$refs.single);

            $owl.trigger("destroy.owl.carousel");
            $owl.html($owl.find(".owl-stage-outer").html()).removeClass("owl-loaded");
            $owl.find(".owl-item").remove();

            const $thumbs = $(this.$refs.thumbs);

            $thumbs.trigger("destroy.owl.carousel");
            $thumbs.html($thumbs.find(".owl-stage-outer").html()).removeClass("owl-loaded");
            $thumbs.find(".owl-item").remove();

            this.initCarousel();
            this.initThumbCarousel();
        },

        initCarousel()
        {
            const imageCount = this.getImageCount();
            const carouselSettings = {
                autoHeight       : true,
                dots             : this.showDots,
                items            : 1,
                lazyLoad         : true, // disabled for fetchpriority debug
                video            : true,
                rewind           : true,
                margin           : 10,
                mouseDrag        : imageCount > 1,
                nav              : imageCount > 1,
                navClass         : [
                    "owl-single-item-nav left carousel-control",
                    "owl-single-item-nav right carousel-control"
                ],
                navContainerClass: "",
                navText          : [
                    "<i class=\"owl-single-item-control fa fa-chevron-left\" aria-hidden=\"true\"></i>",
                    "<i class=\"owl-single-item-control fa fa-chevron-right\" aria-hidden=\"true\"></i>"
                ],
                smartSpeed       : 350,
                onChanged: event =>
                {
                    if (event.property?.name !== "position")
                    {
                        return;
                    }

                    this.syncCurrentItem(event);

                    if (this.initialized && this.currentItem !== 0)
                    {
                        this.hasCarouselInteracted = true;
                    }

                    const $thumb = $(this.$refs.thumbs);

                    $thumb.trigger("to.owl.carousel", [
                        this.currentItem,
                        350
                    ]);
                },
                onInitialized: event =>
                {
                    this.syncCurrentItem(event);
                    this.initialized = true;
                }
            };

            if (this.animationStyle !== "standard")
            {
                carouselSettings.animateOut = this.animationStyle;
            }

            $(this.$refs.single).owlCarousel(carouselSettings);
        },

        syncCurrentItem(event)
        {
            const index = event.relatedTarget.relative(event.item.index);

            this.currentItem = Number.isInteger(index) && index >= 0 && index < this.singleImages.length ? index : 0;
        },

        initThumbCarousel()
        {
            $(this.$refs.thumbs).owlCarousel({
                autoHeight       : true,
                dots             : false,
                items            : 6,
                lazyLoad         : true,
                loop             : false,
                margin           : 5,
                mouseDrag        : false,
                center           : false,
                nav              : true,
                navClass         : [
                    "owl-single-item-nav left carousel-control",
                    "owl-single-item-nav right carousel-control"
                ],
                navContainerClass: "",
                navText          : [
                    "<i class=\"owl-single-item-control fa fa-chevron-left\" aria-hidden=\"true\"></i>",
                    "<i class=\"owl-single-item-control fa fa-chevron-right\" aria-hidden=\"true\"></i>"
                ],
                smartSpeed       : 350,
                onInitialized: event =>
                {
                    this.currentThumbStart = event.page?.index || 0;
                }
            });

            $(this.$refs.thumbs).on("changed.owl.carousel", event =>
            {
                const nextThumbStart = event.item?.index || 0;

                if (nextThumbStart !== 0)
                {
                    this.hasCarouselInteracted = true;
                }

                this.currentThumbStart = nextThumbStart;
            });
        },

        goTo(index)
        {
            if(index < 0) 
            {
                $('#videoModal').modal('toggle');
                return;
            }

            this.hasCarouselInteracted = true;

            const $owl = $(this.$refs.single);

            $owl.trigger("to.owl.carousel", [
                index,
                350
            ]);
        },

        getMetadata(image)
        {
            return getImageMetadata(image && image.alternate, image && image.name, [],
                this.$options.filters.itemName(this.currentVariation));
        },

        getAltText(image)
        {
            return this.getMetadata(image).alt;
        },

        getPreparedImages(accessor)
        {
            if (!this.currentVariation?.images)
            {
                return [];
            }

            const imagesAccessor = this.currentVariation.images.variation?.length ? "variation" : "all";
            const sourceImages = this.currentVariation.images[imagesAccessor] || [];

            return sourceImages
                .map(image => ({
                    id: image.id ?? 0,
                    url: image.url || image[accessor] || "",
                    displayUrl: image[accessor] || image.url || "",
                    position: image.position,
                    alternate: image.names ? image.names.alternate : null,
                    name: image.names ? image.names.name : null,
                    width: image.width ?? null,
                    height: image.height ?? null
                }))
                .sort((imgA, imgB) => imgA.position - imgB.position)
                .slice(0, this.maxQuantity);
        },

        shouldRenderDirectImage(image, index)
        {
            return image && image.position >= 0 && index === 0;
        },

        shouldLoadMainSlide(index)
        {
            if (index <= 0)
            {
                return true;
            }

            if (!this.hasCarouselInteracted)
            {
                return index < 3;
            }

            return Math.abs(index - this.currentItem) <= 1;
        },

        shouldLoadThumb(index)
        {
            if (!this.hasCarouselInteracted)
            {
                return index < 8;
            }

            const visibleThumbStart = Math.max(0, this.currentThumbStart - 1);
            const visibleThumbEnd = this.currentThumbStart + 6;

            return index >= visibleThumbStart && index <= visibleThumbEnd;
        },

        getLightboxUrl(image)
        {
            return image?.url || "";
        },

        getDisplayUrl(image)
        {
            return image?.displayUrl || image?.url || "";
        },

        getImageSrc(image)
        {
            return this.getDisplayUrl(image);
        },

        getImageInitialSrc(image)
        {
            return this.getImageAvifUrl(image) || this.getImageSrc(image);
        },

        getImageAvifUrl(image)
        {
            const imageUrl = this.getImageSrc(image);

            if (!imageUrl || !/\/item\/images\//.test(imageUrl))
            {
                return null;
            }

            if (/\.avif(?:$|\?)/i.test(imageUrl))
            {
                return imageUrl;
            }

            if (/\.(jpe?g|png|webp)(?:$|\?)/i.test(imageUrl))
            {
                return `${imageUrl}.avif`;
            }

            return null;
        },

        handleDirectImageError(event, image)
        {
            const fallbackUrl = this.getImageSrc(image);
            const element = event?.target;

            if (!element || !fallbackUrl || element.dataset.fallbackApplied === "true")
            {
                return;
            }

            element.dataset.fallbackApplied = "true";
            element.src = fallbackUrl;
        },

        getImageName(image)
        {
            return this.getMetadata(image).title || getImageMetadata(this.$options.filters.itemName(this.currentVariation)).alt;
        },

        getImageWidth(image) {
            return image && image.width ? image.width : this.$options.filters.itemImageWidth(this.carouselImages);
        },

        getImageHeight(image) {
            return image && image.height ? image.height : this.$options.filters.itemImageHeight(this.carouselImages);
        },

    }
}
</script>
